import { useEffect, useState, useCallback } from "react";
import api, { apiErr } from "@/lib/api";
import { PageHeader, StatusBadge, TableSkeleton, EmptyState } from "@/components/shared";
import { fmtDate } from "@/lib/format";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { toast } from "sonner";
import { CheckSquare, Clock, AlertTriangle, Filter } from "lucide-react";

const PRIORITY_TONE = { High: "red", Medium: "amber", Low: "gray" };

export default function MyTasks() {
  const [rows, setRows] = useState(null);
  const [summary, setSummary] = useState(null);
  const [filter, setFilter] = useState({ status: "open", due: "all", q: "" });
  const [selected, setSelected] = useState(new Set());

  const load = useCallback(async () => {
    try {
      const params = { page_size: 100 };
      if (filter.status && filter.status !== "all") params.status = filter.status;
      if (filter.due && filter.due !== "all") params.due = filter.due;
      if (filter.q) params.q = filter.q;
      const { data } = await api.get("/tasks", { params });
      setRows(data);
      const s = await api.get("/tasks/summary");
      setSummary(s.data);
    } catch (e) {
      toast.error(apiErr(e));
      setRows({ items: [] });
    }
  }, [filter]);

  useEffect(() => {
    load();
  }, [load]);

  const toggle = (id) => {
    const next = new Set(selected);
    next.has(id) ? next.delete(id) : next.add(id);
    setSelected(next);
  };

  const complete = async (id) => {
    try {
      await api.put(`/tasks/${id}`, { status: "completed" });
      toast.success("Task completed");
      load();
    } catch (e) {
      toast.error(apiErr(e));
    }
  };

  const bulkComplete = async () => {
    if (selected.size === 0) return toast.error("Select tasks first");
    try {
      await api.post(`/tasks/bulk`, {
        ids: [...selected],
        action: "complete",
      });
      toast.success(`${selected.size} completed`);
      setSelected(new Set());
      load();
    } catch (e) {
      toast.error(apiErr(e));
    }
  };

  return (
    <div>
      <PageHeader
        title="My Tasks"
        subtitle="Everything assigned to you across the platform"
      />

      {summary && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
          <Metric
            icon={CheckSquare}
            label="Open"
            value={summary.open}
            tone="text-[#255058]"
          />
          <Metric
            icon={AlertTriangle}
            label="Overdue"
            value={summary.overdue}
            tone="text-red-600"
          />
          <Metric
            icon={Clock}
            label="Due Today"
            value={summary.due_today}
            tone="text-amber-600"
          />
          <Metric
            icon={CheckSquare}
            label="Completed 7d"
            value={summary.completed_7d}
            tone="text-emerald-600"
          />
        </div>
      )}

      <Card className="p-3 mb-3 flex flex-wrap gap-2 items-center">
        <Filter className="h-4 w-4 text-muted-foreground" />
        <Select
          value={filter.status}
          onValueChange={(v) => setFilter({ ...filter, status: v })}
        >
          <SelectTrigger className="w-36" data-testid="task-status-filter">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All</SelectItem>
            <SelectItem value="open">Open</SelectItem>
            <SelectItem value="completed">Completed</SelectItem>
          </SelectContent>
        </Select>
        <Select
          value={filter.due}
          onValueChange={(v) => setFilter({ ...filter, due: v })}
        >
          <SelectTrigger className="w-36" data-testid="task-due-filter">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Any date</SelectItem>
            <SelectItem value="overdue">Overdue</SelectItem>
            <SelectItem value="today">Due today</SelectItem>
            <SelectItem value="upcoming">Next 7 days</SelectItem>
          </SelectContent>
        </Select>
        <Input
          placeholder="Search title..."
          className="w-56"
          value={filter.q}
          onChange={(e) => setFilter({ ...filter, q: e.target.value })}
          data-testid="task-search"
        />
        <div className="ml-auto flex gap-2">
          <Button
            variant="outline"
            disabled={selected.size === 0}
            onClick={bulkComplete}
            data-testid="bulk-complete-btn"
          >
            Complete Selected ({selected.size})
          </Button>
        </div>
      </Card>

      <Card className="overflow-hidden">
        {!rows ? (
          <div className="p-4">
            <TableSkeleton />
          </div>
        ) : rows.items.length === 0 ? (
          <EmptyState
            icon={CheckSquare}
            title="No tasks match"
            description="Try a different filter."
          />
        ) : (
          <div className="overflow-x-auto scrollbar-thin">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-8"></TableHead>
                  <TableHead>Task</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Priority</TableHead>
                  <TableHead>Due</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.items.map((t) => (
                  <TableRow
                    key={t.id}
                    data-testid={`task-row-${t.id}`}
                    className="hover:bg-accent/40"
                  >
                    <TableCell>
                      <Checkbox
                        checked={selected.has(t.id)}
                        onCheckedChange={() => toggle(t.id)}
                        aria-label="select"
                        data-testid={`task-select-${t.id}`}
                      />
                    </TableCell>
                    <TableCell className="font-medium max-w-[300px] truncate">
                      {t.title}
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground">
                      {t.type}
                    </TableCell>
                    <TableCell>
                      <StatusBadge
                        value={t.priority}
                        tone={PRIORITY_TONE[t.priority] || "gray"}
                      />
                    </TableCell>
                    <TableCell className="text-sm">
                      {fmtDate(t.due_at)}
                    </TableCell>
                    <TableCell>
                      <StatusBadge
                        value={t.status}
                        tone={t.status === "completed" ? "green" : "blue"}
                      />
                    </TableCell>
                    <TableCell>
                      {t.status === "open" && (
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => complete(t.id)}
                          data-testid={`task-complete-${t.id}`}
                        >
                          Mark done
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </Card>
    </div>
  );
}

function Metric({ icon: Icon, label, value, tone }) {
  return (
    <Card className="p-3 flex items-center gap-3">
      <Icon className={`h-5 w-5 ${tone}`} />
      <div>
        <div className="text-xs text-muted-foreground">{label}</div>
        <div className="text-xl font-semibold">{value ?? "—"}</div>
      </div>
    </Card>
  );
}
